/**
 * Event agents (admins, Phase 7): an agent drafts in a room when project events
 * happen (a task done, a feature live, a deploy failed). Drafts wait in the room
 * for an admin's Approve, Edit or Discard.
 */
import { useEffect, useState } from "react";
import { ErrorNote, Fold, Modal } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { useStore } from "../lib/store";

interface Sub {
  id: string;
  agentId: string;
  agentName: string;
  roomId: string;
  roomName: string;
  events: string[];
  projectIds: string[];
  brief: string;
  enabled: boolean;
}
interface EventRow {
  id: string;
  type: string;
  title: string;
  project: string;
  createdAt: string;
}
interface Data {
  subscriptions: Sub[];
  events: EventRow[];
  types: { id: string; label: string }[];
}

const PRESETS = [
  { label: "Announcement post", text: "A short LinkedIn post announcing it to customers: what is new and why it helps them, about 100 words, plain and friendly, no hashtags." },
  { label: "Newsletter paragraph", text: "One paragraph for the monthly newsletter about it, about 80 words, clear and concrete." },
  { label: "Team message", text: "A short message to the team saying what changed and anything they should check, three lines at most." },
  { label: "Incident note", text: "A calm incident note for the team: what failed, what it affects, what to check first. Short." },
];

const when = (iso: string) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export function EventAgentSettings() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Sub | "new" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const projects = useStore((s) => s.boot?.projects ?? []);
  const reload = () => api<Data>("/api/settings/event-agents").then(setData, (err) => setError(err instanceof ApiError ? err.message : "Could not load the event agents."));
  useEffect(() => void reload(), []);
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;
  const label = (id: string) => data.types.find((t) => t.id === id)?.label ?? id;
  const projectNames = (ids: string[]) => (ids.length ? ids.map((id) => projects.find((p) => p.id === id)?.name ?? "a removed project").join(", ") : "all projects");

  return (
    <Fold
      id="settings-events"
      anchor="events"
      title="Event agents"
      summary={data.subscriptions.length ? `${data.subscriptions.length} event ${data.subscriptions.length === 1 ? "agent" : "agents"}: ${data.subscriptions.map((s) => `${s.agentName} in #${s.roomName}`).join(", ")}` : "None yet."}
      side={
        <button className="btn primary small" type="button" onClick={() => setEditing("new")}>
          Add an event agent
        </button>
      }
    >
      <p className="muted-text">
        When something happens on a project (a task done, a feature live, a deploy failed), an event agent writes a draft in the room you choose: an announcement, a newsletter paragraph, a note for the team. Drafts wait there for an admin to approve, edit or discard them. Agora publishes nothing outside by itself.
      </p>
      {note && <p className="notice-line ok">{note}</p>}
      {data.subscriptions.length > 0 && (
        <ul className="rows">
          {data.subscriptions.map((s) => (
            <li key={s.id}>
              <div>
                <b>
                  {s.agentName} drafts in #{s.roomName}
                </b>
                <small>
                  {s.events.map(label).join(", ")} · {projectNames(s.projectIds)}
                  {s.enabled ? "" : " · off"}
                </small>
                <small className="muted-text">{s.brief.slice(0, 160)}</small>
              </div>
              <button
                className="btn ghost small"
                type="button"
                title="Send a test event now"
                onClick={async () => {
                  try {
                    await api(`/api/settings/event-agents/${s.id}/try`, {});
                    setNote(`Test event sent. ${s.agentName}'s draft appears in #${s.roomName} in a moment.`);
                  } catch (err) {
                    setNote(err instanceof ApiError ? err.message : "Could not send the test event.");
                  }
                }}
              >
                Try
              </button>
              <button className="btn ghost small" type="button" onClick={() => setEditing(s)}>
                Edit
              </button>
            </li>
          ))}
        </ul>
      )}
      {data.events.length > 0 && (
        <>
          <h3 className="sub-head">Latest events</h3>
          <ul className="rows compact">
            {data.events.map((e) => (
              <li key={e.id}>
                <div>
                  <span>
                    {label(e.type)}: {e.title}
                  </span>
                  <small>
                    {e.project} · {when(e.createdAt)}
                  </small>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {editing && <EventAgentForm sub={editing === "new" ? null : editing} types={data.types} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />}
    </Fold>
  );
}

function EventAgentForm({ sub, types, onClose, onSaved }: { sub: Sub | null; types: { id: string; label: string }[]; onClose: () => void; onSaved: () => void }) {
  const boot = useStore((s) => s.boot)!;
  const agents = boot.people.filter((p) => p.kind === "agent");
  const rooms = boot.rooms.filter((r) => r.kind === "room");
  const [agentId, setAgentId] = useState(sub?.agentId ?? agents[0]?.id ?? "");
  const [roomId, setRoomId] = useState(sub?.roomId ?? rooms.find((r) => /market/i.test(r.name))?.id ?? rooms[0]?.id ?? "");
  const [events, setEvents] = useState<string[]>(sub?.events ?? ["feature_live"]);
  const [projectIds, setProjectIds] = useState<string[]>(sub?.projectIds ?? []);
  const [brief, setBrief] = useState(sub?.brief ?? PRESETS[0]!.text);
  const [enabled, setEnabled] = useState(sub?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const agent = agents.find((a) => a.id === agentId);
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));

  return (
    <Modal title={sub ? "Edit event agent" : "Add an event agent"} onClose={onClose} wide>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const body = { agentId, roomId, events, projectIds, brief, enabled };
            if (sub) await api(`/api/settings/event-agents/${sub.id}`, body, "PATCH");
            else await api("/api/settings/event-agents", body);
            onSaved();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save.");
            setBusy(false);
          }
        }}
      >
        <ErrorNote error={error} />
        <div className="field-row">
          <label className="field">
            <span>Agent</span>
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Drafts in the room</span>
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  #{r.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {agent && agent.agent?.kind !== "api" && <small className="muted-text">{agent.name} runs on the owner's plan: it writes these drafts for the owner.</small>}
        <fieldset className="field">
          <legend>When</legend>
          <div className="check-grid">
            {types.map((t) => (
              <label key={t.id} className="check">
                <input type="checkbox" checked={events.includes(t.id)} onChange={(e) => setEvents(toggle(events, t.id, e.target.checked))} />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="field">
          <legend>On these projects (none ticked: all projects)</legend>
          <div className="check-grid">
            {boot.projects.map((p) => (
              <label key={p.id} className="check">
                <input type="checkbox" checked={projectIds.includes(p.id)} onChange={(e) => setProjectIds(toggle(projectIds, p.id, e.target.checked))} />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="field">
          <span>What to write</span>
          <textarea rows={4} value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={2000} />
        </label>
        <div className="preset-row">
          {PRESETS.map((p) => (
            <button key={p.label} className="btn ghost small" type="button" onClick={() => setBrief(p.text)}>
              {p.label}
            </button>
          ))}
        </div>
        <label className="field">
          <span className="check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            On
          </span>
        </label>
        <div className="actions">
          {sub && (
            <button
              className="btn ghost danger push-left"
              type="button"
              disabled={busy}
              onClick={async () => {
                if (!confirm("Delete this event agent? Its drafts stay in the room.")) return;
                setBusy(true);
                try {
                  await api(`/api/settings/event-agents/${sub.id}`, undefined, "DELETE");
                  onSaved();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : "Could not delete.");
                  setBusy(false);
                }
              }}
            >
              Delete
            </button>
          )}
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || !agentId || !roomId || !events.length || brief.trim().length < 3}>
            {sub ? "Save" : "Add"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
